import { useEffect, useState } from "react";

import type {
  Contract,
  ContractAPIPDFResponse,
  ContractAPIResponse,
} from "./types";
import useFetchContract from "./use-fetch-contract";

export default function useContract(id?: string) {
  const [contract, setContract] = useState<Contract | null>(null);

  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isLoading: useFetchContractIsLoading,
    error: useFetchContractError,
    get,
    create,
    update,
  } = useFetchContract();

  const getContract = async (id: string) => {
    const res = (await get(`contract/${id}`)) as ContractAPIResponse;

    return res.data;
  };

  const getContractPDFURL = async (id: string) => {
    const res = (await get(`contracts/file/${id}`)) as ContractAPIPDFResponse;

    return res.data.url;
  };

  const createContract = async (contract: Contract) => {
    const res = (await create("contracts", contract)) as ContractAPIResponse;
    return res.data;
  };

  const updateContract = async (id: string, contract: Contract) => {
    return await update(`contract/${id}`, contract);
  };

  const approveContract = async (id: string, contract: Contract) => {
    if (id !== "") {
      return await update(`contract/${id}`, contract);
    } else {
      const res = (await create("contracts", contract)) as ContractAPIResponse;
      return await update(`contract/${res.data.id}`, contract, {
        timeout: 50000,
      });
    }
  };

  const signContract = async (id: string, contract: Contract) => {
    if (id !== "") {
      return await create(`contract/${id}/sign`, {
        provider: "DOCUSIGN",
      });
    } else {
      const res = (await create("contracts", contract)) as ContractAPIResponse;

      // setTimeout(() => {
      //   create(
      //     `contract/${res.data.id}/sign`,
      //     {
      //       provider: "DOCUSIGN",
      //     },
      //     {
      //       timeout: 50000,
      //     }
      //   );
      // }, 50000);

      return await create(
        `contract/${res.data.id}/sign`,
        {
          provider: "DOCUSIGN",
        },
        {
          timeout: 50000,
        }
      );
    }
  };

  const voidContract = async (id: string, reason: string) => {
    return await update(`contract/${id}/void`, { reason });
  };

  const terminateContract = async (id: string) => {
    return await update(`contract/${id}/terminate`, undefined);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const contract = (await get(`contract/${id}`)) as ContractAPIResponse;

        setContract(contract.data as any);
      } catch (e) {
        if (e instanceof Error) {
          setError(e);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (id && !contract && !isLoading && !useFetchContractIsLoading && !error) {
      setIsLoading(true);
      fetch();
    }
  }, [id, contract, isLoading, useFetchContractIsLoading, error, get]);

  return {
    contract,
    isLoading: isLoading || useFetchContractIsLoading,
    error: error || useFetchContractError,
    getContract,
    getContractPDFURL,
    createContract,
    updateContract,
    approveContract,
    signContract,
    voidContract,
    terminateContract,
  };
}
